import {z} from "zod/v4-mini"
import {GeometryValueNode} from "#template/nodes/geometry-graph/value/value-node"
import {
    type PointGeometryAttributeLikeNode,
    type CurveGeometryAttributeLikeNode,
    type MeshGeometryAttributeLikeNode,
    pointGeometryAttributeLike,
    curveGeometryAttributeLike,
    meshGeometryAttributeLike,
} from "#template/nodes/geometry-graph/attribute/attribute-node"
import {
    type PointGeometryAttributeSetLikeNode,
    type CurveGeometryAttributeSetLikeNode,
    type MeshGeometryAttributeSetLikeNode,
    pointGeometryAttributeSetLike,
    curveGeometryAttributeSetLike,
    meshGeometryAttributeSetLike,
} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import {PointGeometryAttributeSet} from "#template/nodes/geometry-graph/attribute-set/attribute-set-builder/point-geometry-attribute-set"
import {CurveGeometryAttributeSet} from "#template/nodes/geometry-graph/attribute-set/attribute-set-builder/curve-geometry-attribute-set"
import {MeshGeometryAttributeSet} from "#template/nodes/geometry-graph/attribute-set/attribute-set-builder/mesh-geometry-attribute-set"
import {PointGeometryAttributeSetRecordInput} from "#template/nodes/geometry-graph/attribute-set/attribute-set-record-input/point-geometry-attribute-set-record-input"
import {CurveGeometryAttributeSetRecordInput} from "#template/nodes/geometry-graph/attribute-set/attribute-set-record-input/curve-geometry-attribute-set-record-input"
import {MeshGeometryAttributeSetRecordInput} from "#template/nodes/geometry-graph/attribute-set/attribute-set-record-input/mesh-geometry-attribute-set-record-input"
import type {PointGeometryAttributeSetPointLikeInput} from "#template/nodes/geometry-graph/attribute-set/point-geometry-attribute-set-point-like-input"
import type {CurveGeometryAttributeSetCurveLikeInput} from "#template/nodes/geometry-graph/attribute-set/curve-geometry-attribute-set-curve-like-input"
import type {MeshGeometryAttributeSetMeshLikeInput} from "#template/nodes/geometry-graph/attribute-set/mesh-geometry-attribute-set-mesh-like-input"
import type {PointGeometryJoinOperator} from "#template/nodes/geometry-graph/attribute-set/join-operator/point-geometry-join-operator"
import type {CurveGeometryJoinOperator} from "#template/nodes/geometry-graph/attribute-set/join-operator/curve-geometry-join-operator"
import type {MeshGeometryJoinOperator} from "#template/nodes/geometry-graph/attribute-set/join-operator/mesh-geometry-join-operator"
import type {PointGeometryProductOperator} from "#template/nodes/geometry-graph/attribute-set/product-operator/point-geometry-product-operator"
import type {CurveGeometryProductOperator} from "#template/nodes/geometry-graph/attribute-set/product-operator/curve-geometry-product-operator"
import type {MeshGeometryProductOperator} from "#template/nodes/geometry-graph/attribute-set/product-operator/mesh-geometry-product-operator"
import type {PointGeometryPartitionOperator} from "#template/nodes/geometry-graph/attribute-set/partition-operator/point-geometry-partition-operator"
import type {CurveGeometryPartitionOperator} from "#template/nodes/geometry-graph/attribute-set/partition-operator/curve-geometry-partition-operator"
import type {MeshGeometryPartitionOperator} from "#template/nodes/geometry-graph/attribute-set/partition-operator/mesh-geometry-partition-operator"

// Cross-dim aggregates — unions over the per-dim variants. Useful for inspectors that
// handle "any attribute-like" regardless of dim. For dim-specific narrowing prefer the
// dim-specific marker predicates (`isPointGeometryAttributeLikeNode`, etc.).

export type GeometryAttributeLikeNode = PointGeometryAttributeLikeNode | CurveGeometryAttributeLikeNode | MeshGeometryAttributeLikeNode
export type GeometryAttributeSetLikeNode = PointGeometryAttributeSetLikeNode | CurveGeometryAttributeSetLikeNode | MeshGeometryAttributeSetLikeNode
export type GeometryAttributeValueLike = GeometryValueNode
export type GeometryAttributeSet = PointGeometryAttributeSet | CurveGeometryAttributeSet | MeshGeometryAttributeSet
export type GeometryAttributeSetInputLike =
    | PointGeometryAttributeSetRecordInput
    | CurveGeometryAttributeSetRecordInput
    | MeshGeometryAttributeSetRecordInput
    | PointGeometryAttributeSetPointLikeInput
    | CurveGeometryAttributeSetCurveLikeInput
    | MeshGeometryAttributeSetMeshLikeInput

export const isGeometryAttributeValueLike = (x: unknown): x is GeometryAttributeValueLike => x instanceof GeometryValueNode
export const isGeometryAttributeSet = (x: unknown): x is GeometryAttributeSet =>
    x instanceof PointGeometryAttributeSet || x instanceof CurveGeometryAttributeSet || x instanceof MeshGeometryAttributeSet

export const getGeometryAttributeLike: z.ZodMiniType<GeometryAttributeLikeNode, GeometryAttributeLikeNode> = z.union([
    pointGeometryAttributeLike,
    curveGeometryAttributeLike,
    meshGeometryAttributeLike,
])

export const getGeometryAttributeSetLike: z.ZodMiniType<GeometryAttributeSetLikeNode, GeometryAttributeSetLikeNode> = z.union([
    pointGeometryAttributeSetLike,
    curveGeometryAttributeSetLike,
    meshGeometryAttributeSetLike,
])

export const createPointGeometryAttributeSetFromSet = (
    name: string,
    record: Record<string, PointGeometryAttributeLikeNode | PointGeometryAttributeSetLikeNode | null>,
) => new PointGeometryAttributeSet({parameters: {name, input: new PointGeometryAttributeSetRecordInput({parameters: {record}})}})

export const createCurveGeometryAttributeSetFromSet = (
    name: string,
    record: Record<string, CurveGeometryAttributeLikeNode | CurveGeometryAttributeSetLikeNode | null>,
) => new CurveGeometryAttributeSet({parameters: {name, input: new CurveGeometryAttributeSetRecordInput({parameters: {record}})}})

export const createMeshGeometryAttributeSetFromSet = (
    name: string,
    record: Record<string, MeshGeometryAttributeLikeNode | MeshGeometryAttributeSetLikeNode | null>,
) => new MeshGeometryAttributeSet({parameters: {name, input: new MeshGeometryAttributeSetRecordInput({parameters: {record}})}})

export type JoinOperatorLike = PointGeometryJoinOperator | CurveGeometryJoinOperator | MeshGeometryJoinOperator
export type ProductOperatorLike = PointGeometryProductOperator | CurveGeometryProductOperator | MeshGeometryProductOperator
export type PartitionOperatorLike = PointGeometryPartitionOperator | CurveGeometryPartitionOperator | MeshGeometryPartitionOperator
